import { ActionModal, TextInput } from "@/components";
import { ValidationPatterns } from "@/utils/form-validation";
import InputErrorMessage from "@/components/input-error-message";
import { StyledSelectBoxContainer } from "@/pages/TDCLIENT/users/add-edit-user-modal/styles";
import { Box, Grid, Typography } from "@mui/material";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { MultiCheckbox } from "@/components";
import HierarchicalCheckbox from "@/components/checkbox/hierarchical";
import { PAGE_OPTIONS, filterOptionsByAvailable, normalizeForStorage, expandForDisplay, collectAllKeys } from "@/roles/protection/pageOptions";
import { useGetAllClientsQuery } from "@/services";
import { useGetTenantAllowedPagesQuery, useSetPartnerAccessibleClientsMutation, useSetUserAllowedPagesMutation } from "@/graphql/page-access";
import { useSnackbar } from "@/hooks";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { tokens } from "@/tokens";

type UpdatePartnerModalProps = {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  partnerDetails: Pick<
    SPOwnerPartnerTypes,
    "firstName" | "lastName" | "_id" | "email"
  > | null;
  handleUpdatePartner: (e: SPOwnerPartnerTypes) => void;
  btnLoader: boolean;
};

const UpdatePartnerModal: React.FC<UpdatePartnerModalProps> = ({
  show,
  setShow,
  partnerDetails,
  handleUpdatePartner,
  btnLoader = false,
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SPOwnerPartnerTypes>({
    defaultValues: {
      firstName: partnerDetails ? partnerDetails.firstName : "",
      lastName: partnerDetails ? partnerDetails.lastName : "",
      email: partnerDetails ? partnerDetails.email : "",
    },
  });

  // Determine if TD Admin is managing an SP tenant
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);
  const user = useAppSelector((s) => s.auth.user);
  const isTDAdminSP =
    user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "SERVICE_PROVIDER";

  // Load clients for selection (tenant-aware for TD Admin)
  const { data: clientsResp } = useGetAllClientsQuery({
    pageNo: 1,
    pageSize: 100,
    searchKeyword: "",
    ...(isTDAdminSP && { tenantId: selectedTenant?._id }),
  });
  const clients = clientsResp?.data ?? [];
  const [selectedClientIds, setSelectedClientIds] = React.useState<string[]>(() =>
    (partnerDetails as any)?.accessibleClients || []
  );
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery(
    isTDAdminSP ? { tenantId: selectedTenant?._id } : undefined
  );
  const [allowedPages, setAllowedPages] = React.useState<string[]>(
    expandForDisplay(((partnerDetails as any)?.allowedPages as string[]) || [], PAGE_OPTIONS)
  );
  const availablePageKeys = tenantPagesResp?.pages || [];
  const filteredOptions = filterOptionsByAvailable(PAGE_OPTIONS, availablePageKeys);
  const [setPartnerClients, { isLoading: savingClients }] = useSetPartnerAccessibleClientsMutation();
  const [setUserAllowedPages, { isLoading: savingPages }] = useSetUserAllowedPagesMutation();
  const { show: showSnackBar } = useSnackbar();

  const onSubmit = handleSubmit(async (data) => {
    // First update basic partner fields
    await handleUpdatePartner(data);
    // Then attempt to persist client selections; if it fails, inform the user and keep the modal open
    if (partnerDetails?._id) {
      try {
        await setPartnerClients({ partnerId: partnerDetails._id, clientIds: selectedClientIds }).unwrap();
      } catch (error: any) {
        showSnackBar(
          error?.data?.message || error?.message || "Details saved; assigning clients failed. Please retry.",
          "",
          "error"
        );
        // continue to attempt pages below
      }
      try {
        const validKeys = collectAllKeys(filteredOptions);
        const finalPages = normalizeForStorage((allowedPages || []).filter((k) => validKeys.has(k)), PAGE_OPTIONS);
        await setUserAllowedPages({ userId: partnerDetails._id, pages: finalPages }).unwrap();
      } catch (error: any) {
        showSnackBar(
          error?.data?.message || error?.message || "Details saved; assigning pages failed. Please retry.",
          "",
          "error"
        );
      }
    }
  });

  const formFields: Array<{
    name: keyof SPOwnerPartnerTypes;
    label: string;
    type: string;
    rules: object;
    placeholder: string;
    disabled?: boolean;
  }> = [
    {
      name: "firstName",
      label: "First Name",
      type: "text",
      rules: { required: "First name is required" },
      placeholder: "Enter First name",
    },
    {
      name: "lastName",
      label: "Last Name",
      type: "text",
      rules: { required: "Last name is required" },
      placeholder: "Enter Last name",
    },
    {
      name: "email",
      label: "Email Address",
      type: "email",
      placeholder: "hi@example.com",
      disabled: true,
      rules: {
        required: "Email is required",
        pattern: {
          value: ValidationPatterns.email,
          message: "Invalid email address",
        },
      },
    },
  ];

  return (
    <ActionModal
      show={show}
      setShow={setShow}
      buttonAction={onSubmit}
      isConfirmButtonLoading={btnLoader || savingClients || savingPages}
      buttonText="Update"
      title="Update Partner"
      subTitle="Update the partner details below"
      modalStyles={{
        "& .MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded": {
          width: "500px",
        },
      }}
    >
      <Box
        component="form"
        onSubmit={onSubmit}
      >
        <StyledSelectBoxContainer>
          <Grid container spacing={2}>
            {formFields.map((field) => (
              <Grid
                item
                xs={12}
                sm={field.name === "email" ? 12 : 6}
                key={field.name}
              >
                <Controller
                  name={field.name}
                  control={control}
                  rules={field.rules}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <TextInput
                        label={field.label}
                        disabled={field.disabled}
                        type={field.type}
                        placeholder={field.placeholder}
                        value={value as string}
                        handleChangeValue={onChange}
                        error={!!errors[field.name]}
                      />
                    );
                  }}
                />
                <InputErrorMessage message={errors[field.name]?.message} />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 1 }} fontWeight={tokens.fontWeight.semibold} fontSize={tokens.fontSize.lg} variant="caption">
              Assign Clients
            </Typography>
            <MultiCheckbox
              label="Clients"
              checkboxes={clients.map((c) => ({ label: c.firstName }))}
              checked={clients.map((c) => selectedClientIds.includes(c._id))}
              handleParentChange={(e) => {
                const checked = e.target.checked;
                if (checked) setSelectedClientIds(clients.map((c) => c._id));
                else setSelectedClientIds([]);
              }}
              handleChildChange={(index) => () => {
                const id = clients[index]?._id;
                if (!id) return;
                setSelectedClientIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                );
              }}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 1 }} fontWeight={tokens.fontWeight.semibold} fontSize={tokens.fontSize.lg} variant="caption">
              Pages
            </Typography>
            <HierarchicalCheckbox
              options={filteredOptions}
              selectedKeys={allowedPages}
              onChange={setAllowedPages}
            />
            {filteredOptions.length === 0 && (
              <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.base, mt: 0.5 }}>
                No tenant pages configured. Contact your administrator.
              </Typography>
            )}
          </Box>
        </StyledSelectBoxContainer>
      </Box>
    </ActionModal>
  );
};

export default UpdatePartnerModal;
